'use client';

import React, { useState, useEffect, useMemo } from 'react';

import { tryCatchCreateElement } from '@/helpers/component_helper'
import { getFileUrl, getTranslationFunction, getFormFetcher } from '@/app/utils/mixed_csr_ssr';
import { clsx } from 'clsx';
import { createEntry, createOrder } from '@/app/utils/directus';
import { calculateNumberOfDays, isValidDateRange } from '@/app/utils/pricing';
import { useSearchParams } from 'next/navigation'
import { useReCaptcha } from "next-recaptcha-v3";

import * as lucideIcons from 'lucide-react';
import Container from '@/components/shared/Container';
import Image from 'next/image';
import Typography from '@/components/shared/Typography';
import Button from '@/components/shared/forms/Button';
import Input from '@/components/shared/forms/Input';
import Textarea from '@/components/shared/forms/Textarea';
import Select from '@/components/shared/forms/Select';

const FormNew = ({ params, form_key, linked_item_id = null, className }) => {
    const { executeRecaptcha } = useReCaptcha();

    const feePercentage = 30;

    const { config, locale, langCode } = params;
    const { settings, toursData, cruisesData } = config;
    const { t } = getTranslationFunction(config, locale);
    const { getFormByKey } = getFormFetcher(config, locale);

    const form = getFormByKey(form_key);

    const [data, setData] = useState({
        slug_locale: locale,
        locale: langCode
    });
    const [success, setSuccess] = useState(null);
    const [warning, setWarning] = useState(null);
    const [error, setError] = useState(null);

    const [submitDisabled, setSubmitDisabled] = useState(false);
    const [createOrderOrEntry, setCreateOrderOrEntry] = useState('entry');

    const [tours, setTours] = useState(null);
    const [cruises, setCruises] = useState(null);

    const [toursInputVisible, setToursInputVisible] = useState(false);
    const [cruisesInputVisible, setCruisesInputVisible] = useState(false);

    const searchParams = useSearchParams()
    const infoBase64 = searchParams.get('info');
    const info = infoBase64 ? JSON.parse(atob(infoBase64)) : {};

    const [showOnRequestText, setShowOnRequestText] = useState(true);
    const [totalPrice, setTotalPrice] = useState(0);
    const [feePrice, setFeePrice] = useState(0);
    const [subtotalPrice, setSubtotalPrice] = useState(0);
    const [showDiscount, setShowDiscount] = useState(false);
    const [priceCategories, setPriceCategories] = useState([]);
    const [numberOfDays, setNumberOfDays] = useState(1);
    const [hasPerDayCategory, setHasPerDayCategory] = useState(false);
    const [priceBreakdown, setPriceBreakdown] = useState(null);

    const [loading, setLoading] = useState(false);

    const [availabilityBlocked, setAvailabilityBlocked] = useState(false);
    const [availabilityMessage, setAvailabilityMessage] = useState(null);
    const [priceOverrideMessage, setPriceOverrideMessage] = useState(null);
    const [priceOverridePeriod, setPriceOverridePeriod] = useState(null);

    useEffect(() => {
        const tempData = {};

        if (form.name === 'booking') {
            setCruises(cruisesData.map((option) => {
                return {
                    ...option.translated_content,
                    ...option,
                    label: option.translated_content.title,
                    value: option.id
                };
            }));
        } else if (form.name === 'tour-booking') {
            setTours(toursData.map((option) => {
                return {
                    ...option.translated_content,
                    ...option,
                    label: option.translated_content.title,
                    value: option.id
                };
            }));
        } else if (form.key.includes('brochure')) {
            if (form.key.includes('-brochure')) {
                if (linked_item_id) {
                    tempData.linked_item_id = linked_item_id;
                }

                if (form.key.includes('tour')) {
                    tempData.linked_item = 'tours';
                } else if (form.key.includes('cruise')) {
                    tempData.linked_item = 'cruises';
                }
            }

            tempData.type = form.name;

            setData({
                ...data,
                ...tempData
            });
        }

    }, [form, toursData, cruisesData]);

    useEffect(() => {
        const tempData = {};

        if (form.name === 'booking') {

            setToursInputVisible(false);
            setCruisesInputVisible(true);

            tempData.linked_item = 'cruises';

            if (info) {
                if (info['item-id']) {
                    tempData.linked_item_id = info['item-id'];
                }
            }

            setData({
                ...data,
                ...tempData
            });
        } else if (form.name === 'tour-booking') {

            setToursInputVisible(true);
            setCruisesInputVisible(false);

            tempData.linked_item = 'tours';

            if (info) {
                if (info['item-id']) {
                    tempData.linked_item_id = info['item-id'];
                }
            }

            setData({
                ...data,
                ...tempData
            });
        }

    }, [tours, cruises]);

    useEffect(() => {
        if (form.name !== 'tour-booking' || !data.linked_item_id) {
            setPriceCategories([]);
            return;
        }

        fetch(`/api/tour-price-categories?tour_id=${data.linked_item_id}&lang_code=${langCode}`)
            .then(res => res.json())
            .then(json => setPriceCategories(json.data ?? []))
            .catch(() => setPriceCategories([]));
    }, [data.linked_item_id]);

    const priceCategoryGroups = useMemo(() => {
        const groups = new Map();

        priceCategories.forEach(cat => {
            const groupId = cat.group?.id ?? 'ungrouped';
            if (!groups.has(groupId)) {
                groups.set(groupId, {
                    id: groupId,
                    sort: cat.group?.sort ?? 0,
                    label: cat.group?.translated_content?.name ?? null,
                    required: cat.group?.required ?? false,
                    categories: [],
                });
            }
            groups.get(groupId).categories.push(cat);
        });

        return Array.from(groups.values()).sort((a, b) => a.sort - b.sort);
    }, [priceCategories]);

    const priceCategoryFieldName = (groupId) => `price_category_group_${groupId}`;

    useEffect(() => {
        setData(prev => {
            let changed = false;
            const next = { ...prev };

            Object.keys(next).forEach(key => {
                if (!key.startsWith('price_category_group_')) return;

                const group = priceCategoryGroups.find(g => priceCategoryFieldName(g.id) === key);
                const stillValid = group?.categories.some(cat => String(cat.id) === String(next[key]));
                if (!stillValid) {
                    delete next[key];
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    }, [priceCategoryGroups]);

    const applyPriceCalculation = (overridePeriod) => {
        if (form.name !== 'tour-booking') return;
        if (!data.linked_item_id || !tours) return;

        const tour = tours.find((tourLoop) => tourLoop.id === parseInt(data.linked_item_id));
        if (!tour) {
            setSubmitDisabled(false);
            setWarning(null);
            setShowOnRequestText(true);
            setPriceBreakdown(null);
            return;
        }

        const selectedByGroup = priceCategoryGroups.map(group => ({
            group,
            category: group.categories.find(
                cat => String(cat.id) === String(data[priceCategoryFieldName(group.id)])
            ),
        }));

        const missingRequiredGroup = selectedByGroup.find(({ group, category }) => group.required && !category);
        const selectedCategories = selectedByGroup.map(({ category }) => category).filter(Boolean);

        const categoryMinimums = selectedCategories
            .map(cat => cat.minimum_number_of_people ? parseInt(cat.minimum_number_of_people) : null)
            .filter(v => v !== null);
        const minimumNumberOfPeople = categoryMinimums.length > 0
            ? Math.max(...categoryMinimums)
            : (tour.minimum_number_of_people ? parseInt(tour.minimum_number_of_people) : 1);
        const numberOfPeople = data.number_of_people ? parseInt(data.number_of_people) : null;

        const numberOfDaysBooked = calculateNumberOfDays(data.date_from, data.date_to);
        setNumberOfDays(numberOfDaysBooked);
        setHasPerDayCategory(selectedCategories.some(cat => cat.price_calculation_method === 'per_person_per_day'));

        const isBaseOverridden = overridePeriod?.eur_price_override != null;
        const basePrice = isBaseOverridden
            ? parseFloat(overridePeriod.eur_price_override)
            : (tour.eur_price ? parseFloat(tour.eur_price) : 0);

        const categoryLineItems = selectedCategories.map(cat => {
            const catOverride = overridePeriod?.category_prices?.find(
                cp => String(cp.tour_price_categories_id) === String(cat.id)
            );
            const isOverridden = catOverride?.eur_price_override != null;
            const unitPrice = isOverridden ? parseFloat(catOverride.eur_price_override) : parseFloat(cat.eur_price ?? 0);
            const calculationMethod = cat.price_calculation_method ?? 'per_person';
            const perDay = calculationMethod === 'per_person_per_day';
            const days = perDay ? numberOfDaysBooked : 1;

            return {
                label: cat.translated_content?.name ?? String(cat.id),
                unitPrice,
                calculationMethod,
                perDay,
                days,
                isOverridden,
                lineTotal: unitPrice * days,
            };
        });

        const categoryAddOn = categoryLineItems.reduce((sum, item) => sum + item.lineTotal, 0);

        const price = basePrice + categoryAddOn;

        const requestOnly = tour.request_only ?? false;

        if (requestOnly || price === 0) {
            setCreateOrderOrEntry('entry');
            setShowOnRequestText(true);
            setPriceBreakdown(null);
        } else {
            setCreateOrderOrEntry('order');
            setShowOnRequestText(false);

            if (price > 0) {
                let totalPrice = price;
                let feePrice = price * (feePercentage / 100);

                const peopleCount = parseInt(data.number_of_people) > 0 ? parseInt(data.number_of_people) : 1;
                totalPrice = totalPrice * peopleCount;
                feePrice = feePrice * peopleCount;

                const discountApplied = parseInt(data.number_of_people) >= 4;

                setPriceBreakdown({
                    isBaseOverridden,
                    basePrice,
                    categoryLineItems,
                    categoryAddOn,
                    pricePerPerson: price,
                    peopleCount,
                    subtotal: totalPrice,
                    discountApplied,
                    feePercentage,
                    feePrice: discountApplied ? feePrice * 0.9 : feePrice,
                    total: discountApplied ? totalPrice * 0.9 : totalPrice,
                });

                if (discountApplied) {
                    setSubtotalPrice(totalPrice);
                    setTotalPrice(totalPrice * 0.9);
                    setFeePrice(feePrice * 0.9);
                    setShowDiscount(true);
                } else {
                    setSubtotalPrice(totalPrice);
                    setTotalPrice(totalPrice);
                    setFeePrice(feePrice);
                    setShowDiscount(false);
                }
            }
        }

        if (missingRequiredGroup) {
            setSubmitDisabled(true);
            setWarning(t('required', { field: missingRequiredGroup.group.label ?? t('price_category') }));
        } else if (!isValidDateRange(data.date_from, data.date_to)) {
            setSubmitDisabled(true);
            setWarning(t('invalid_date_range'));
        } else if (minimumNumberOfPeople !== null && numberOfPeople !== null && numberOfPeople < minimumNumberOfPeople) {
            setSubmitDisabled(true);
            setWarning(t('minimum_people_warning', { minimum_number_of_people: minimumNumberOfPeople, people_plural: parseInt(minimumNumberOfPeople) > 1 ? t('people') : t('person') }));
        } else {
            setSubmitDisabled(false);
            setWarning(null);
        }
    };

    useEffect(() => {
        if (form.name !== 'tour-booking' || !data.linked_item_id || !data.date_from) {
            setAvailabilityBlocked(false);
            setAvailabilityMessage(null);
            setPriceOverrideMessage(null);
            setPriceOverridePeriod(null);
            return;
        }

        const params = new URLSearchParams({ tour_id: data.linked_item_id, date_from: data.date_from, lang_code: langCode });
        if (data.date_to) params.set('date_to', data.date_to);

        fetch(`/api/availability?${params}`)
            .then(res => res.json())
            .then(json => {
                if (json.blocked) {
                    setAvailabilityBlocked(true);
                    const p = json.period;
                    const start = p?.start_date ? new Date(p.start_date).toLocaleDateString('nl-NL') : '';
                    const end = p?.end_date ? new Date(p.end_date).toLocaleDateString('nl-NL') : '';
                    setAvailabilityMessage(`${t('booking_not_possible', { start: start, end: end })}: ${json.reason}.`);
                    setPriceOverrideMessage(null);
                    setPriceOverridePeriod(null);
                    applyPriceCalculation(null);
                } else {
                    setAvailabilityBlocked(false);
                    setAvailabilityMessage(null);

                    const overridePeriod = json.price_override || null;
                    setPriceOverridePeriod(overridePeriod);
                    applyPriceCalculation(overridePeriod);

                    if (overridePeriod) {
                        const p = overridePeriod;
                        const start = p.start_date ? new Date(p.start_date).toLocaleDateString('nl-NL') : '';
                        const end = p.end_date ? new Date(p.end_date).toLocaleDateString('nl-NL') : '';
                        setPriceOverrideMessage(t('seasonal_price', { name: p.translated_content?.name, start: start, end: end }));
                    } else {
                        setPriceOverrideMessage(null);
                    }
                }
            })
            .catch(() => {
                setAvailabilityBlocked(false);
                setAvailabilityMessage(null);
                setPriceOverrideMessage(null);
                setPriceOverridePeriod(null);
                applyPriceCalculation(null);
            });
    }, [data.linked_item_id, data.date_from, data.date_to]);

    useEffect(() => {
        applyPriceCalculation(priceOverridePeriod);
    }, [data, priceCategories, priceOverridePeriod]);

    async function validateRecaptcha(token) {
        return new Promise(async (resolve, reject) => {
            try {
                const googleResponse = await fetch('/api/recaptcha', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        token: token
                    })
                });

                const googleData = await googleResponse.json();
                if (!googleData.success) {
                    reject();
                }

                resolve(true);
            } catch (error) {
                console.error('Captcha Form error', error);
                reject();
            }
        });
    }

    const getFieldLabel = (field) => {
        if (field.name.startsWith('price_category_group_')) {
            const groupId = field.name.slice('price_category_group_'.length);
            const group = priceCategoryGroups.find(g => String(g.id) === groupId);
            if (group?.label) return group.label;
        }
        return field.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (submitDisabled || availabilityBlocked) {
            return;
        }

        const allFields = e.target.querySelectorAll('input, select, textarea');
        for (const field of allFields) {
            if (field.required) {
                const fieldElementType = field.tagName;
                switch (fieldElementType) {
                    case "SELECT":
                        if (field.value === 'select' || !field.value) {
                            setError(t('required', {
                                field: getFieldLabel(field)
                            }));
                            return;
                        }
                        break;
                    case "INPUT":
                        if (field.type === 'checkbox') {
                            if (!field.checked) {
                                setError(t('required', {
                                    field: getFieldLabel(field)
                                }));
                                return;
                            }
                        } else if (!field.value) {
                            setError(t('required', {
                                field: getFieldLabel(field)
                            }));
                            return;
                        }
                        break;
                    case "TEXTAREA":
                        if (!field.value) {
                            setError(t('required', {
                                field: getFieldLabel(field)
                            }));
                            return;
                        }
                        break;
                    default:
                        if (!field.value) {
                            setError(t('required', {
                                field: getFieldLabel(field)
                            }));
                            return;
                        }
                        break;
                }
            }
        }

        setLoading(true);

        executeRecaptcha("form_submit").then((token) => {
            validateRecaptcha(token).then(() => {
                if (createOrderOrEntry === 'order') {
                    console.log('Create Order', {
                        id: parseInt(e.target.id),
                        data: data,
                        linked_item_id: parseInt(data.linked_item_id),
                    });

                    createOrder(e.target.id, data, data.linked_item_id).then((res) => {
                        if (typeof res === 'object') {
                            if (res.success) {
                                if (res.data) {
                                    if (res.data.payment && res.data.payment.payment_url) {
                                        setSuccess(t('form_success'));
                                        setLoading(false);
                                        e.target.reset();

                                        // Redirect to payment url
                                        window.location.href = res.data.payment.payment_url;
                                    } else {
                                        console.warn('No payment url in successful response');
                                        setError(t('form_error'));
                                        setLoading(false);
                                    }
                                } else {
                                    setError(t('form_error'));
                                    setLoading(false);
                                    console.warn('No data in successful response');
                                }
                            } else {
                                console.error('Create Order error', res.message);
                                setError(t('form_error'));
                                setLoading(false);
                            }
                        } else {
                            setSuccess(res);
                            setLoading(false);
                            e.target.reset();
                        }
                    }).catch((err) => {
                        console.error('Create Order error', err);
                        setError(t('form_error'));
                        setLoading(false);
                    });
                } else {
                    const linkedItem = data.linked_item ? data.linked_item : info.collection;
                    const linkedItemId = data.linked_item ? parseInt(data.linked_item_id) : parseInt(info['item-id']);

                    console.log('Create Entry', {
                        id: parseInt(e.target.id),
                        data: data,
                        linked_item: linkedItem,
                        linked_item_id: linkedItemId,
                    });

                    createEntry(e.target.id, data, linkedItem, linkedItemId).then((res) => {
                        setLoading(false);
                        setSuccess(t(res));
                        e.target.reset();
                    }).catch((err) => {
                        console.error('Create Entry error', err);
                        setError(t('form_error'));
                        setLoading(false);
                    });
                }
            }).catch((err) => {
                console.error('Captcha Form error', err);
                setError(t('form_error'));
                setLoading(false);
            });
        }).catch((err) => {
            console.error('Captcha Form error', err);
            setError(t('form_error'));
            setLoading(false);
        });
    }

    const handleChange = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.value
        });
    };

    if (!form) {
        return null;
    }

    return (
        <>
            <Container className={clsx('-mt-44 z-10 relative scroll-mt-40 pb-20', className)} id={`request-${form.key}`}>
                <form id={form.id} onSubmit={handleSubmit} className=' lg:w-2/3 mx-auto rounded-4xl bg-secondary-100 flex flex-col gap-y-8 p-8 items-center relative'>
                    <div className='w-28 h-28 -mt-20'>
                        {settings.logo_no_text && (
                            <Image className="w-auto h-full mx-auto" src={getFileUrl(settings.logo_no_text)} alt={'Logo'} width={200} height={50} />
                        )}
                    </div>

                    {!form.translated_content?.title && !form.translated_content?.short_description ? null : (
                        <div className='flex flex-col items-center'>
                            {form.translated_content.title && (
                                <Typography variant='h3' className='w-fit uppercase text-primary-800 font-bold'>
                                    {form.translated_content?.title}
                                </Typography>
                            )}

                            {form.translated_content?.short_description && (
                                <Typography variant='p' className='w-fit max-w-lg text-primary-800 text-center'>
                                    {form.translated_content?.short_description}
                                </Typography>
                            )}
                        </div>
                    )}

                    {form.groups && form.groups.map((group, groupIndex) => (
                        <React.Fragment key={groupIndex}>
                            <div className={`w-full flex flex-col ${group.data.group_is_hidden ? 'hidden' : ''}`}>
                                {(group.data.translated_content && group.data.translated_content.label && form.show_group_labels) && (
                                    <Typography variant='p' className='w-fit text-primary-800'>
                                        {group.data.translated_content.label}
                                    </Typography>
                                )}

                                <div className='w-full bg-primary-800 rounded-md p-2 '>
                                    {group.data.fields && group.data.fields.map((field, fieldIndex) => {
                                        const isLastField = fieldIndex == group.data.fields.length - 1;
                                        const isBeforeLastField = fieldIndex == group.data.fields.length - 2;

                                        let classname = `border-secondary-100 w-full`

                                        if (field.data.width === 'full') {
                                            classname += ` border-b last:border-b-0`
                                        } else { // half
                                            classname += ` lg:w-1/2`
                                            if (isLastField || isBeforeLastField) {
                                                if (isLastField) {
                                                    classname += ' border-b-0'
                                                } else if (isBeforeLastField) {
                                                    // check if last field is full width or not
                                                    if (group.data.fields[fieldIndex + 1].data.width === 'full') {
                                                        classname += ' border-b'
                                                    } else {
                                                        classname += ' border-b lg:border-b-0'
                                                    }
                                                }
                                            } else {
                                                classname += ' border-b'
                                            }
                                        }

                                        const spreadProps = {}
                                        if (field.data.readonly) {
                                            spreadProps.readOnly = true
                                            spreadProps.disabled = true

                                            if (field.data.key === 'info' && info) {
                                                if (field.data.type && field.data.type !== 'dropdown' && field.data.type !== 'textarea') {
                                                    spreadProps.value = JSON.stringify(info)
                                                }
                                            }
                                        }

                                        return (
                                            <React.Fragment key={fieldIndex}>
                                                {(field.data.type && field.data.type == 'textarea') ? (
                                                    <Textarea {...spreadProps} required={field.data.required} onChange={handleChange} name={field.data.key} rows={field.data.row_count} placeholder={field.data.translated_content?.placeholder ?? ''} className={classname} />
                                                ) : (field.data.type && field.data.type == 'dropdown') ? (
                                                    field.data.key === 'linked_item' ? (
                                                        <>
                                                            {toursInputVisible && (
                                                                <Select {...spreadProps} value={info && info['item-id'] ? info['item-id'] : 'select'} required={field.data.required} onChange={handleChange} name='linked_item_id' options={tours} placeholder={field.data.translated_content?.placeholder ?? t('select_option')} className={`${classname} cursor-pointer`} params={params} />
                                                            )}

                                                            {cruisesInputVisible && (
                                                                <Select {...spreadProps} value={info && info['item-id'] ? info['item-id'] : 'select'} required={field.data.required} onChange={handleChange} name='linked_item_id' options={cruises} placeholder={field.data.translated_content?.placeholder ?? t('select_option')} className={`${classname} cursor-pointer`} params={params} />
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Select {...spreadProps} required={field.data.required} onChange={handleChange} name={field.data.key} options={field.data.options ?? []} placeholder={field.data.translated_content?.placeholder ?? t('select_option')} className={`${classname} cursor-pointer`} params={params} />
                                                    )
                                                ) : (
                                                    <Input {...spreadProps} required={field.data.required} onChange={handleChange} name={field.data.key} placeholder={field.data.translated_content?.placeholder ?? ''} type={field.data.type ?? 'text'} className={classname} />
                                                )}
                                            </React.Fragment>
                                        )
                                    })}
                                </div>
                            </div>

                            {(groupIndex == form.groups.length - 1) && (
                                <>
                                    {form.name === 'tour-booking' && (
                                        <>
                                            {priceCategoryGroups.map(group => {
                                                const fieldName = priceCategoryFieldName(group.id);
                                                return (
                                                    <div key={group.id} className='w-full flex flex-col'>
                                                        <Typography variant='p' className='w-fit text-primary-800'>
                                                            {group.label ?? t('price_category')}
                                                        </Typography>
                                                        <div className='w-full bg-primary-800 rounded-md p-2'>
                                                            <Select
                                                                key={data.linked_item_id + '-' + group.id + '-' + data[fieldName]}
                                                                name={fieldName}
                                                                required={group.required}
                                                                onChange={handleChange}
                                                                value={group.categories.some(c => String(c.id) === String(data[fieldName])) ? data[fieldName] : 'select'}
                                                                options={group.categories.map(cat => ({ label: cat.translated_content?.name, value: cat.id }))}
                                                                placeholder={t('select_price_category')}
                                                                className='w-full cursor-pointer'
                                                                params={params}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {showOnRequestText ? (
                                                <div className='w-full flex gap-x-2'>
                                                    <Typography variant='h6' className='w-fit text-primary-800'>
                                                        {t('total_price')}: {t('on_request')}
                                                    </Typography>
                                                </div>
                                            ) : (
                                                <>
                                                    {process.env.NODE_ENV !== 'production' && priceBreakdown && (
                                                        <div className='w-full flex flex-col gap-1 p-3 rounded-md bg-black/80 text-white text-xs font-mono'>
                                                            <span className='text-yellow-400 font-bold'>DEV ONLY: price breakdown</span>
                                                            <span>
                                                                Base price{priceBreakdown.isBaseOverridden ? ' (seasonal override)' : ''}: €{priceBreakdown.basePrice.toFixed(2)}
                                                            </span>
                                                            {priceBreakdown.categoryLineItems.map((item, index) => (
                                                                <span key={index}>
                                                                    {item.label} [{item.calculationMethod}]: €{item.unitPrice.toFixed(2)}{item.perDay ? ` × ${item.days} days` : ''} = €{item.lineTotal.toFixed(2)}{item.isOverridden ? ' (override)' : ''}
                                                                </span>
                                                            ))}
                                                            <span>Per-person price: €{priceBreakdown.pricePerPerson.toFixed(2)}</span>
                                                            <span>× {priceBreakdown.peopleCount} {priceBreakdown.peopleCount === 1 ? 'person' : 'people'} = €{priceBreakdown.subtotal.toFixed(2)}</span>
                                                            {priceBreakdown.discountApplied && (
                                                                <span>-10% discount (4+ people)</span>
                                                            )}
                                                            <span>Pre-payment fee ({priceBreakdown.feePercentage}%): €{priceBreakdown.feePrice.toFixed(2)}</span>
                                                            <span className='font-bold'>Total: €{priceBreakdown.total.toFixed(2)}</span>
                                                        </div>
                                                    )}

                                                    {showDiscount && (
                                                        <div className='w-full flex flex-col gap-2'>
                                                            <>
                                                                <Typography variant='h6' className='w-fit text-primary-800'>
                                                                    {t('subtotal_price')}: {subtotalPrice.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })}
                                                                </Typography>
                                                                <Typography variant='p' className='w-fit text-primary-800'>
                                                                    -10% {t('discount_price_notice')}
                                                                </Typography>
                                                            </>
                                                        </div>
                                                    )}

                                                    <div className='w-full flex-col gap-2'>
                                                        <Typography variant='h6' className='w-fit text-primary-800'>
                                                            {t('total_price')}: {totalPrice.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })}
                                                        </Typography>
                                                        <Typography variant='p' className='w-fit text-primary-800'>
                                                            {t('pre_payment_label')}: {feePrice.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })}
                                                        </Typography>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                    {(success || error || warning || availabilityMessage || priceOverrideMessage) && (
                                        <div className='flex flex-col gap-1'>
                                            {success && (
                                                <Typography variant='p' className='text-green-500 font-semibold'>
                                                    {success}
                                                </Typography>
                                            )}

                                            {priceOverrideMessage && (
                                                <Typography variant='p' className='text-yellow-500 font-semibold'>
                                                    {priceOverrideMessage}
                                                </Typography>
                                            )}

                                            {availabilityMessage && (
                                                <Typography variant='p' className='text-red-500 font-semibold'>
                                                    {availabilityMessage}
                                                </Typography>
                                            )}

                                            {warning && (
                                                <Typography variant='p' className='text-yellow-500 font-semibold'>
                                                    {warning}
                                                </Typography>
                                            )}

                                            {error && (
                                                <Typography variant='p' className='text-red-500 font-semibold'>
                                                    {error}
                                                </Typography>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </React.Fragment>
                    ))}

                    {form.require_consent_checkbox == true && (
                        <div className='w-full'>
                            <Input name='consent_checkbox' id='consent_checkbox' required={true} type='checkbox' label={t('consent_checkbox_description')} params={params} />
                        </div>
                    )}

                    {/* <pre>
                        {JSON.stringify(data, null, 2)}
                    </pre> */}

                    <div>
                        <Button disabled={submitDisabled || availabilityBlocked} type='submit' variant={`${form.button_variant ? form.button_variant : 'tertiary'}`}>
                            {form.name === 'tour-booking' ? (
                                showOnRequestText ? (
                                    t('request_booking')
                                ) : (
                                    t('confirm_booking')
                                )
                            ) : (
                                form && form.translated_content && form.translated_content.button_text ? form.translated_content.button_text : 'Submit'
                            )}
                        </Button>
                    </div>
                    {form.name === 'tour-booking' && (
                        <>
                            {!showOnRequestText && (
                                <div className="max-w-lg mx-auto">
                                    <Typography variant='p' className='text-center text-primary-800'>
                                        {t('secure_payment_note')}
                                    </Typography>
                                </div>
                            )}
                        </>
                    )}

                    {loading && (
                        <div className="absolute inset-0 bg-secondary-100 opacity-50 rounded-4xl flex items-center justify-center z-10">
                            <div className="animate-spin w-fit h-fit">
                                {tryCatchCreateElement(lucideIcons['Loader'], { size: 32 })}
                            </div>
                        </div>
                    )}
                </form>
            </Container>
        </>
    );
}

export default FormNew;